<template>
  <div class="segmented-progress-bar">
    <div class="segmented-progress-bar__segments">
      <div
        class="segmented-progress-bar__segment segmented-progress-bar__segment_ab"
        :class="{ [CSS_HIGHLIGHT_CLASS]: pointInAB }"
      >
        <div
          v-if="segmentLabels && segmentLabels.ab"
          class="segmented-progress-bar__segment-label"
        >
          {{ segmentLabels.ab }}
        </div>
        <div class="segmented-progress-bar__segment-inner">
          <div
            v-if="hasPoint && pointInAB"
            class="segmented-progress-bar__point"
            :style="pointStyles"
          />
        </div>
      </div>

      <div
        class="segmented-progress-bar__segment segmented-progress-bar__segment_bc"
        :class="{ [CSS_HIGHLIGHT_CLASS]: pointInBC }"
      >
        <div
          v-if="segmentLabels?.bc"
          class="segmented-progress-bar__segment-label"
        >
          {{ segmentLabels.bc }}
        </div>
        <div class="segmented-progress-bar__segment-inner">
          <div
            v-if="hasPoint && pointInBC"
            class="segmented-progress-bar__point"
            :style="pointStyles"
          />
        </div>
      </div>

      <div
        class="segmented-progress-bar__segment segmented-progress-bar__segment_cd"
        :class="{ [CSS_HIGHLIGHT_CLASS]: pointInCD }"
      >
        <div
          v-if="segmentLabels?.cd"
          class="segmented-progress-bar__segment-label"
        >
          {{ segmentLabels.cd }}
        </div>
        <div class="segmented-progress-bar__segment-inner">
          <div
            v-if="hasPoint && pointInCD"
            class="segmented-progress-bar__point"
            :style="pointStyles"
          />
        </div>
      </div>
    </div>

    <template v-if="marksType">
      <div
        v-if="isMarksTypeMinMidMax"
        class="segmented-progress-bar__marks segmented-progress-bar__marks_type_min-mid-max"
      >
        <div class="segmented-progress-bar__mark segmented-progress-bar__mark_min">
          {{ mark(min) }}
        </div>
        <div class="segmented-progress-bar__mark segmented-progress-bar__mark_mid">
          {{ mark(mid) }}
        </div>
        <div class="segmented-progress-bar__mark segmented-progress-bar__mark_max">
          {{ mark(max) }}
        </div>
      </div>

      <div
        v-if="isMarksTypeSegments"
        class="segmented-progress-bar__marks segmented-progress-bar__marks_type_segments"
      >
        <div class="segmented-progress-bar__mark segmented-progress-bar__mark_min">
          {{ mark(bc.b) }}
        </div>
        <div class="segmented-progress-bar__mark segmented-progress-bar__mark_max">
          {{ mark(bc.c) }}
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  PropType,
} from 'vue';

import formatNumericValueToDisplay from '@/domains/formatNumericValueToDisplay';

import { ESegmentedProgressBarMarksType } from './constants';

export default defineComponent({
  name: 'SegmentedProgressBar',
  props: {
    min: {
      type: Number,
      required: true,
    },
    mid: {
      type: Number,
      required: true,
    },
    max: {
      type: Number,
      required: true,
    },
    point: {
      type: [Number, null] as PropType<number | null>,
      required: true,
    },
    segmentLabels: {
      type: [Object, null] as PropType<{
        ab?: string,
        bc?: string,
        cd?: string,
      } | null>,
      default: null,
    },
    marksType: {
      type: [String, null] as PropType<ESegmentedProgressBarMarksType | null>,
      default: ESegmentedProgressBarMarksType.minMidMax,
    },
    markPrefix: {
      type: String,
      default: '',
    },
    markSuffix: {
      type: String,
      default: '',
    },
  },
  setup(props, { expose }) {
    const isMarksTypeMinMidMax = computed(() => props.marksType === ESegmentedProgressBarMarksType.minMidMax);
    const isMarksTypeSegments = computed(() => props.marksType === ESegmentedProgressBarMarksType.segments);

    const ab = computed(() => ({
      a: props.min,
      b: props.min + ((props.mid - props.min) / 2),
    }));

    const cd = computed(() => ({
      c: props.max - ((props.max - props.mid) / 2),
      d: props.max,
    }));

    const bc = computed(() => ({
      b: ab.value.b,
      c: cd.value.c,
    }));

    const hasPoint = computed(() => props.point !== null);

    const pointInAB = computed(() => props.point !== null && props.point < ab.value.b);
    const pointInBC = computed(
      () => props.point !== null && props.point >= bc.value.b && props.point <= bc.value.c,
    );
    const pointInCD = computed(() => props.point !== null && props.point > cd.value.c);

    const pointPositionPercent = computed<null | number>(() => {
      if (props.point === null) return null;

      const processData: {
        per100: null | number,
        point: null | number,
      } = {
        per100: null,
        point: null,
      };

      if (pointInAB.value) {
        processData.per100 = ab.value.b - ab.value.a;
        processData.point = props.point - ab.value.a;
      }

      if (pointInBC.value) {
        processData.per100 = bc.value.c - bc.value.b;
        processData.point = props.point - bc.value.b;
      }

      if (pointInCD.value) {
        processData.per100 = cd.value.d - cd.value.c;
        processData.point = props.point - cd.value.c;
      }

      if (processData.point && processData.per100) {
        let result = (processData.point * 100) / processData.per100;
        if (result < 0) result = 0;
        if (result > 100) result = 100;

        return result;
      }

      return null;
    });

    const pointStyles = computed(() => ({ left: `${pointPositionPercent.value}%` }));

    const CSS_HIGHLIGHT_CLASS = 'segmented-progress-bar__segment_highlight';

    const mark = computed(() => (
      markText,
    ) => `${props.markPrefix} ${formatNumericValueToDisplay(markText)} ${props.markSuffix}`.trim());

    expose({
      pointInAB,
      pointInBC,
      pointInCD,
    });

    return {
      CSS_HIGHLIGHT_CLASS,
      bc,
      hasPoint,
      isMarksTypeMinMidMax,
      isMarksTypeSegments,
      mark,
      pointInAB,
      pointInBC,
      pointInCD,
      pointStyles,
    };
  },
});
</script>

<style lang="scss" src="./styles.scss" />
